import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createMeeting, transcribeAndExtract } from '../lib/api';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, CalendarBlank, SpinnerGap, Sparkle, Microphone, Stop, Waveform } from '@phosphor-icons/react';
import { format } from 'date-fns';

export default function NewMeetingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [date, setDate] = useState(new Date());
  const [showVoiceDialog, setShowVoiceDialog] = useState(false);
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  
  const {
    isRecording,
    recordingTime,
    audioBlob,
    startRecording,
    stopRecording,
    resetRecording,
    formatTime
  } = useVoiceRecorder();
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_company: '',
    meeting_type: 'call',
    key_points: '',
    next_steps: '',
    deal_value: '',
    deal_stage: 'prospecting',
    deal_status: 'open',
    notes: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleVoiceClick = async () => {
    setShowVoiceDialog(true);
    try {
      await startRecording();
    } catch (err) {
      setError('Failed to access microphone. Please allow microphone access.');
      setShowVoiceDialog(false);
    }
  };

  const handleStopAndProcess = async () => {
    setIsProcessingVoice(true);
    
    try {
      // stopRecording now returns a Promise with the blob
      const blob = await stopRecording();
      
      if (blob && blob.size > 0) {
        const response = await transcribeAndExtract(blob);
        const { extracted_data } = response.data;
        
        if (extracted_data) {
          setFormData(prev => ({
            ...prev,
            client_name: extracted_data.client_name || prev.client_name,
            client_email: extracted_data.client_email || prev.client_email,
            client_company: extracted_data.client_company || prev.client_company,
            meeting_type: extracted_data.meeting_type || prev.meeting_type,
            key_points: extracted_data.key_points || prev.key_points,
            next_steps: extracted_data.next_steps || prev.next_steps,
            deal_value: extracted_data.deal_value ? String(extracted_data.deal_value) : prev.deal_value,
            deal_stage: extracted_data.deal_stage || prev.deal_stage,
            notes: extracted_data.notes || prev.notes
          }));
        }
      }
    } catch (err) {
      console.error('Voice processing error:', err);
      setError('Failed to process voice recording. Please try again or fill manually.');
    } finally {
      setIsProcessingVoice(false);
      setShowVoiceDialog(false);
      resetRecording();
    }
  };

  const handleCancelVoice = () => {
    if (isRecording) {
      stopRecording();
    }
    resetRecording();
    setShowVoiceDialog(false);
    setIsProcessingVoice(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const meetingData = {
        ...formData,
        meeting_date: date.toISOString(),
        deal_value: formData.deal_value ? parseFloat(formData.deal_value) : null
      };
      
      await createMeeting(meetingData);
      navigate('/meetings');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save meeting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto animate-entrance" data-testid="new-meeting-page">
      {/* Header */}
      <div className="mb-8">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-[#86868B] hover:text-[#1D1D1F] transition-colors mb-4"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#1D1D1F]">
              Record Meeting
            </h1>
            <p className="text-[#86868B] mt-2">
              Capture key details from your client meeting. AI will automatically generate follow-up tasks.
            </p>
          </div>
          <Button 
            type="button"
            onClick={handleVoiceClick}
            className="btn-accent h-12 px-6 rounded-xl shrink-0"
            data-testid="voice-fill-btn"
          >
            <Microphone size={20} className="mr-2" />
            Voice Fill
          </Button>
        </div>
      </div>

      {/* Voice Recording Dialog */}
      <Dialog open={showVoiceDialog} onOpenChange={handleCancelVoice}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isProcessingVoice ? 'Processing...' : 'Voice Recording'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-8 text-center">
            {isProcessingVoice ? (
              <>
                <div className="w-20 h-20 mx-auto mb-4 bg-[#007AFF]/10 rounded-full flex items-center justify-center">
                  <SpinnerGap size={40} className="text-[#007AFF] animate-spin" />
                </div>
                <p className="text-[#86868B]">Transcribing and extracting details...</p>
              </>
            ) : (
              <>
                <div className={`w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  isRecording ? 'bg-[#FF3B30] animate-pulse' : 'bg-[#007AFF]'
                }`}>
                  {isRecording ? (
                    <Waveform size={48} className="text-white" weight="bold" />
                  ) : (
                    <Microphone size={48} className="text-white" weight="bold" />
                  )}
                </div>
                
                {isRecording && (
                  <p className="text-3xl font-mono font-bold text-[#1D1D1F] mb-2">
                    {formatTime(recordingTime)}
                  </p>
                )}
                
                <p className="text-sm text-[#86868B] mb-6">
                  {isRecording 
                    ? 'Describe your meeting: client, company, discussion, next steps...'
                    : 'Starting microphone...'
                  }
                </p>
                
                {isRecording && (
                  <Button 
                    onClick={handleStopAndProcess}
                    className="btn-primary h-12 px-8 rounded-xl"
                    data-testid="stop-voice-btn"
                  >
                    <Stop size={20} className="mr-2" />
                    Stop & Fill Form
                  </Button>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="meeting-error">
            {error}
          </div>
        )}

        {/* Client Information */}
        <div className="card-base p-6 space-y-6">
          <h2 className="text-lg font-semibold text-[#1D1D1F] border-b border-gray-200 pb-3">
            Client Information
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-2">
              <Label htmlFor="client_name" className="text-[#1D1D1F] font-medium">
                Client Name <span className="text-[#FF3B30]">*</span>
              </Label>
              <Input
                id="client_name"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                placeholder="John Smith"
                required
                autoComplete="name"
                className="input-base h-12 rounded-xl text-base"
                data-testid="client-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_email" className="text-[#1D1D1F] font-medium">
                Client Email
              </Label>
              <Input
                id="client_email"
                name="client_email"
                type="email"
                value={formData.client_email}
                onChange={handleChange}
                placeholder="john@company.com"
                autoComplete="email"
                inputMode="email"
                className="input-base h-12 rounded-xl text-base"
                data-testid="client-email-input"
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="client_company" className="text-[#1D1D1F] font-medium">
                Company
              </Label>
              <Input
                id="client_company"
                name="client_company"
                value={formData.client_company}
                onChange={handleChange}
                placeholder="Acme Corporation"
                className="input-base h-12 rounded-xl text-base"
                data-testid="client-company-input"
              />
            </div>
          </div>
        </div>

        {/* Meeting Details */}
        <div className="card-base p-6 space-y-6">
          <h2 className="text-lg font-semibold text-[#1D1D1F] border-b border-gray-200 pb-3">
            Meeting Details
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-[#1D1D1F] font-medium">
                Meeting Date <span className="text-[#FF3B30]">*</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-left font-normal h-12 rounded-xl border-gray-200 hover:bg-gray-50"
                    data-testid="meeting-date-trigger"
                  >
                    <CalendarBlank size={18} className="mr-2 text-gray-400" />
                    {format(date, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => d && setDate(d)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-[#1D1D1F] font-medium">
                Meeting Type <span className="text-[#FF3B30]">*</span>
              </Label>
              <Select value={formData.meeting_type} onValueChange={(v) => handleSelectChange('meeting_type', v)}>
                <SelectTrigger className="h-12 rounded-xl border-gray-200" data-testid="meeting-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Phone Call</SelectItem>
                  <SelectItem value="video">Video Call</SelectItem>
                  <SelectItem value="in-person">In-Person</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="key_points" className="text-[#1D1D1F] font-medium">
              Key Points Discussed <span className="text-[#FF3B30]">*</span>
            </Label>
            <Textarea
              id="key_points"
              name="key_points"
              value={formData.key_points}
              onChange={handleChange}
              placeholder="What were the main topics discussed? What did the client express interest in?"
              required
              rows={4}
              className="input-base rounded-xl resize-none"
              data-testid="key-points-input"
            />
            <p className="text-xs text-[#86868B] flex items-center gap-1">
              <Sparkle size={14} className="text-[#007AFF]" />
              AI will use this to generate follow-up tasks
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="next_steps" className="text-[#1D1D1F] font-medium">
              Next Steps / Action Items
            </Label>
            <Textarea
              id="next_steps"
              name="next_steps"
              value={formData.next_steps}
              onChange={handleChange}
              placeholder="What are the agreed next steps? Any commitments made?"
              rows={3}
              className="input-base rounded-xl resize-none"
              data-testid="next-steps-input"
            />
          </div>
        </div>

        {/* Deal Information */}
        <div className="card-base p-6 space-y-6">
          <h2 className="text-lg font-semibold text-[#1D1D1F] border-b border-gray-200 pb-3">
            Deal Information
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="deal_value" className="text-[#1D1D1F] font-medium">
                Deal Value ($)
              </Label>
              <Input
                id="deal_value"
                name="deal_value"
                type="number"
                value={formData.deal_value}
                onChange={handleChange}
                placeholder="10000"
                className="input-base h-12 rounded-xl"
                data-testid="deal-value-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#1D1D1F] font-medium">
                Deal Stage
              </Label>
              <Select value={formData.deal_stage} onValueChange={(v) => handleSelectChange('deal_stage', v)}>
                <SelectTrigger className="h-12 rounded-xl border-gray-200" data-testid="deal-stage-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prospecting">Prospecting</SelectItem>
                  <SelectItem value="qualification">Qualification</SelectItem>
                  <SelectItem value="proposal">Proposal</SelectItem>
                  <SelectItem value="negotiation">Negotiation</SelectItem>
                  <SelectItem value="closed">Closed Won</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#1D1D1F] font-medium">
                Deal Status
              </Label>
              <Select value={formData.deal_status} onValueChange={(v) => handleSelectChange('deal_status', v)}>
                <SelectTrigger className="h-12 rounded-xl border-gray-200" data-testid="deal-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="closed_won">Won</SelectItem>
                  <SelectItem value="closed_lost">Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes" className="text-[#1D1D1F] font-medium">
              Additional Notes
            </Label>
            <Textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Any other relevant information..."
              rows={3}
              className="input-base rounded-xl resize-none"
              data-testid="notes-input"
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end gap-4 pt-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate(-1)}
            className="btn-secondary h-12 px-6 rounded-xl"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={loading}
            className="btn-primary h-12 px-8 rounded-xl"
            data-testid="save-meeting-btn"
          >
            {loading ? (
              <>
                <SpinnerGap size={20} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Sparkle size={20} className="mr-2" />
                Save & Generate Tasks
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
